import React from "react";

import { useTranslation } from "react-i18next";

import { formatCount, formatPaisaToRupees } from "../../utils/formatCurrency";

const FinanceBreakdownCard = ({
  title,
  subtitle,
  rows = [],
  totalLabel,
  totalValue,
  loading = false,
}) => {
  const { t } = useTranslation();

  return (
    <div className="bg-white border border-gray-200 rounded-2xl shadow-xl p-6 h-full flex flex-col">
      <div className="mb-5">
        <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
        {subtitle ? (
          <p className="text-xs text-gray-500 mt-1">{subtitle}</p>
        ) : null}
      </div>

      {loading ? (
        <div className="animate-pulse space-y-4 flex-1">
          {[1, 2, 3, 4].map((item) => (
            <div key={item} className="flex justify-between">
              <div className="h-4 w-32 bg-gray-200 rounded" />
              <div className="h-4 w-20 bg-gray-200 rounded" />
            </div>
          ))}
          <div className="border-t border-gray-200 pt-4 flex justify-between">
            <div className="h-5 w-16 bg-gray-200 rounded" />
            <div className="h-5 w-24 bg-gray-200 rounded" />
          </div>
        </div>
      ) : (
        <>
          <div className="flex flex-col gap-3 flex-1">
            {rows.map((row) => (
              <div
                key={row.key || row.label}
                className="flex items-center justify-between gap-4 text-sm"
              >
                <span className="text-gray-600">{row.label}</span>
                <span className="font-medium text-gray-900 tabular-nums">
                  {row.displayValue}
                </span>
              </div>
            ))}
          </div>
          <div className="border-t border-gray-200 mt-5 pt-4 flex items-center justify-between gap-4">
            <span className="font-semibold text-gray-800">
              {totalLabel || t("finance:total")}
            </span>
            <span className="text-lg font-bold text-cfPrimary tabular-nums">
              {totalValue}
            </span>
          </div>
        </>
      )}
    </div>
  );
};

export const buildIssuanceBreakdownRows = (summary, t) => {
  if (!summary) return { rows: [], totalValue: formatPaisaToRupees(0) };

  const gstLabel =
    summary.gst_percentage != null
      ? t("finance:gst_with_percent", { percent: summary.gst_percentage })
      : t("finance:gst");

  return {
    rows: [
      {
        key: "credentials",
        label: t("finance:credentials_issued"),
        displayValue: formatCount(summary.credentials_issued),
      },
      {
        key: "rate",
        label: t("finance:rate_per_credential"),
        displayValue: formatPaisaToRupees(summary.rate_per_credential),
      },
      {
        key: "subtotal",
        label: t("finance:subtotal"),
        displayValue: formatPaisaToRupees(summary.subtotal),
      },
      {
        key: "gst",
        label: gstLabel,
        displayValue: formatPaisaToRupees(summary.gst_amount),
      },
    ],
    totalValue: formatPaisaToRupees(summary.total_amount),
  };
};

export const buildSettlementBreakdownRows = (summary, t) => {
  if (!summary) return { rows: [], totalValue: formatPaisaToRupees(0) };

  const gstLabel =
    summary.gst_percentage != null
      ? t("finance:gst_with_percent", { percent: summary.gst_percentage })
      : t("finance:gst");

  return {
    rows: [
      {
        key: "fee",
        label: t("finance:verification_fee_line", {
          count: formatCount(summary.total_verifications),
          rate: formatPaisaToRupees(summary.verification_fee_rate),
        }),
        displayValue: formatPaisaToRupees(summary.verification_fee_total),
      },
      {
        key: "gst",
        label: gstLabel,
        displayValue: formatPaisaToRupees(summary.gst_amount),
      },
    ],
    totalValue: formatPaisaToRupees(summary.amount_owed),
  };
};

export default FinanceBreakdownCard;
